import { useFragment, graphql } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';

// types
import { type usePdpSellerDetails_user$key as User } from './__generated__/usePdpSellerDetails_user.graphql';
import {
    type usePdpSellerDetails_seller$key as Seller,
    type usePdpSellerDetails_seller$data as SellerData,
} from './__generated__/usePdpSellerDetails_seller.graphql';

const companyName = defineMessages({
    default: {
        id: 'useSellerProfileData.default',
        defaultMessage: 'About the Seller',
    },
});

const userFragment = graphql`
    fragment usePdpSellerDetails_user on User {
        canShowSellerName
    }
`;

const sellerFragment = graphql`
    fragment usePdpSellerDetails_seller on Seller {
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        sellerProfile {
            company
        }
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                        ... on Video {
                            url
                        }
                    }
                }
            }
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
    }
`;

const hasMediaForBioModal = (
    mediaList: NonNullable<
        NonNullable<SellerData['liveStorefrontProfile']>['galleryAlbum']
    >['mediaList']
): boolean => {
    let hasVideo = false;
    const eligebleMedia =
        mediaList?.filter(media => {
            const element = media?.element;
            const { url, path } = element || {};
            if (url) {
                hasVideo = true;
            }
            return path || url;
        }) || [];

    return hasVideo || eligebleMedia.length > 1;
};

export const usePdpSellerDetails = ({
    user: userRef,
    seller: sellerRef,
}: {
    user: User | null;
    seller: Seller | null;
}): {
    imagePath: string | null;
    name: string | null;
    location: string | null;
    shouldShowBioModal: boolean;
} => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const { isRewardsTier2, liveStorefrontProfile, sellerProfile, shippingAddress } = seller || {};

    // location logic
    const location = shippingAddress?.displayFullCityStateCountry || null;

    // profile image logic
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};

    let imagePath = null;

    if (isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath) {
        imagePath = headshotPath;
    } else if (galleryAlbum?.mediaList?.length) {
        imagePath =
            galleryAlbum.mediaList.find(media => media?.element?.path)?.element?.path || null;
    }

    // display name logic
    const { canShowSellerName } = user || {};
    const companyProfileName = sellerProfile?.company;

    const name =
        canShowSellerName && companyProfileName
            ? companyProfileName
            : intl.formatMessage(companyName.default);

    return {
        name,
        location,
        imagePath,
        shouldShowBioModal: !isRewardsTier2 && hasMediaForBioModal(galleryAlbum?.mediaList || null),
    };
};
